// =============================================================================
// api/profiles.ts
// =============================================================================
import type { Profile, UpdateProfilePayload } from "@/types";
import { supabase } from "@cl/supabase";
import { handleSupabaseError } from "./_error";
import { deleteAvatar, uploadAvatar } from "./media";

const PROFILES_TABLE = "profiles" as const;
// Public-safe projection (RLS-restricted table blocks anon SELECT).
const PUBLIC_PROFILE_VIEW = "v_public_profile" as const;
const ERR_NOT_AUTH = "Not authenticated";

export const fetchOwnProfile = async (userId?: string): Promise<Profile> => {
  const uid =
    userId ?? (await supabase.auth.getSession())?.data?.session?.user?.id;
  if (!uid) throw new Error(ERR_NOT_AUTH);

  const { data, error } = await supabase
    .from(PROFILES_TABLE)
    .select("*")
    .eq("id", uid)
    .single();

  if (error) handleSupabaseError(error, "fetchOwnProfile");
  return data as Profile;
};

export const fetchPublicProfile = async (
  userId: string,
): Promise<Pick<
  Profile,
  | "id"
  | "name"
  | "avatar_url"
  | "contact_whatsapp"
  | "contact_phone"
  | "contact_email"
  | "website_url"
  | "profile_kind"
  | "headline"
  | "bio"
  | "social_instagram"
  | "social_linkedin"
  | "social_facebook"
  | "translations"
> | null> => {
  const { data, error } = await supabase
    .from(PUBLIC_PROFILE_VIEW)
    .select(
      "id, name, avatar_url, contact_whatsapp, contact_phone, contact_email, website_url, profile_kind, headline, bio, translations, social_instagram, social_linkedin, social_facebook",
    )
    .eq("id", userId)
    .maybeSingle();

  if (error) handleSupabaseError(error, "fetchPublicProfile");
  return data;
};

export const updateOwnProfile = async (
  updates: UpdateProfilePayload,
): Promise<void> => {
  const uid = (await supabase.auth.getSession())?.data?.session?.user?.id;
  if (!uid) throw new Error(ERR_NOT_AUTH);

  const { error } = await supabase
    .from(PROFILES_TABLE)
    .update(updates)
    .eq("id", uid);

  if (error) handleSupabaseError(error, "updateOwnProfile");
};

export const fetchProfilesBatch = async (
  userIds: string[],
): Promise<Pick<Profile, "id" | "name" | "avatar_url" | "username">[]> => {
  if (!userIds.length) return [];
  const { data, error } = await supabase
    .from(PUBLIC_PROFILE_VIEW)
    .select("id, name, avatar_url, username")
    .in("id", userIds);
  if (error) handleSupabaseError(error, "fetchProfilesBatch");
  return data ?? [];
};

export const fetchAllProfilesWithEmail = async () => {
  const { data, error } = await supabase.rpc("get_profiles_with_email");
  if (error) handleSupabaseError(error, "fetchAllProfilesWithEmail");
  return data;
};

// HALLAZGO 18: SECURITY WARNING
// This function returns id + name for ALL users. Requires admin RLS policy on profiles table.
// If used in non-admin contexts, allows user enumeration (attacker can list all user IDs).
// Current usage: test suite only. Before using in production, verify RLS restricts to admin role.
export const fetchAllProfiles = async (): Promise<
  Pick<Profile, "id" | "name">[]
> => {
  const { data, error } = await supabase
    .from(PROFILES_TABLE)
    .select("id, name")
    .order("name");

  if (error) handleSupabaseError(error, "fetchAllProfiles");
  return data ?? [];
};

export const updateOwnAvatar = async (file: File): Promise<string> => {
  const uid = (await supabase.auth.getSession())?.data?.session?.user?.id;
  if (!uid) throw new Error(ERR_NOT_AUTH);

  const { data: profile } = await supabase
    .from(PROFILES_TABLE)
    .select("avatar_url")
    .eq("id", uid)
    .single();

  const newUrl = await uploadAvatar(uid, file);

  const { error } = await supabase
    .from(PROFILES_TABLE)
    .update({ avatar_url: newUrl })
    .eq("id", uid);

  if (error) {
    await deleteAvatar(newUrl);
    handleSupabaseError(error, "updateOwnAvatar");
  }

  if (profile?.avatar_url) await deleteAvatar(profile.avatar_url);

  return newUrl;
};

export const removeOwnAvatar = async (): Promise<void> => {
  const uid = (await supabase.auth.getSession())?.data?.session?.user?.id;
  if (!uid) throw new Error(ERR_NOT_AUTH);

  const { data: profile } = await supabase
    .from(PROFILES_TABLE)
    .select("avatar_url")
    .eq("id", uid)
    .single();

  const { error } = await supabase
    .from(PROFILES_TABLE)
    .update({ avatar_url: null })
    .eq("id", uid);

  if (error) handleSupabaseError(error, "removeOwnAvatar");

  if (profile?.avatar_url) await deleteAvatar(profile.avatar_url);
};

export const getEmailByUsername = async (
  username: string,
): Promise<string | null> => {
  const { data, error } = await supabase.rpc("get_email_by_username", {
    p_username: username,
  });
  if (error || !data) return null;
  return data as string;
};
